import { useEffect, useState } from 'react';
import { Box, Button as MuiButton, IconButton, Tooltip, Typography } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { FileCopyOutlined, LinkOutlined, MailOutline } from '@material-ui/icons';
import { observer } from 'mobx-react';
import { ConfirmDialog } from '@roc/ui';
import { isNil } from '@roc/feature-utils';
import { renderToString } from 'react-dom/server';
import { useStore } from '../../hooks';
import { getRequestIdVerificationEmailBodyContent, getRequestIdVerificationSubject } from '../creditBackground/requestIdVerification';

const useStyles = makeStyles((theme: Theme) => ({
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    minWidth: 0,
    maxWidth: '100%',
    overflow: 'hidden',
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#FAFAFA',
    border: '1px solid #E0E0E0',
    borderRadius: 8,
  },
  linkIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 32,
    height: 32,
    borderRadius: 6,
    backgroundColor: '#E8EFFE',
    color: '#7B9FE8',
    '& svg': {
      fontSize: '1.1rem',
    },
  },
  details: {
    // width 0 + flex 1: fills the free space but never asks for the link's full width, so the
    // line truncates instead of widening the dialog
    flex: 1,
    width: 0,
    minWidth: 0,
    overflow: 'hidden',
  },
  title: {
    fontWeight: 700,
    fontSize: '0.875rem',
  },
  subtitle: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  email: {
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  link: {
    fontFamily: 'monospace',
  },
  copyButton: {
    flexShrink: 0,
    width: 32,
    height: 32,
    padding: 0,
    border: '1px solid #E0E0E0',
    borderRadius: 6,
    backgroundColor: '#FFFFFF',
    color: '#7B9FE8',
  },
  emailButton: {
    flexShrink: 0,
    whiteSpace: 'nowrap',
    fontSize: '0.875rem',
  },
}));

export type IdentityLinkInformationProps = {
  borrowerId: number;
  loan: any;
};

export const IdentityLinkInformation = observer(({ borrowerId, loan }: IdentityLinkInformationProps) => {
  const classes = useStyles();
  const { borrowerSetupToDoStore, requestIdVerificationStore, globalStore } = useStore();
  const { currentBorrower } = borrowerSetupToDoStore;
  const { messageConfig } = requestIdVerificationStore;
  const [confirmOpen, setConfirmOpen] = useState(false);

  // the borrower's name and email, for the email to the borrower
  useEffect(() => {
    borrowerSetupToDoStore.loadCurrentBorrower(borrowerId, loan?.loanId);
  }, [borrowerId, loan?.loanId]);

  useEffect(() => {
    if (!isNil(borrowerId)) {
      requestIdVerificationStore.getMessageConfig(borrowerId, loan?.lenderId);
    }
  }, [borrowerId, loan?.lenderId]);

  const copyWithSelection = (text: string, container: HTMLElement) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    container.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    container.removeChild(textarea);
    return copied;
  };

  const handleCopy = async event => {
    const link = messageConfig?.link;
    const container = event?.currentTarget?.parentElement ?? document.body;
    let copied = false;
    try {
      await navigator.clipboard.writeText(link);
      copied = true;
    } catch (e) {
      copied = copyWithSelection(link, container);
    }
    if (copied) {
      globalStore.notificationStore.showSuccessNotification({
        message: 'Link was copied to clipboard!',
      });
    } else {
      globalStore.notificationStore.showErrorNotification({
        message: 'Could not copy the link. Please select it and copy it manually.',
      });
    }
  };

  const subject = getRequestIdVerificationSubject();
  const bodyContent = getRequestIdVerificationEmailBodyContent(currentBorrower?.fullName, messageConfig?.link, messageConfig?.tpoName);

  const handleSendEmail = () => {
    const emailBodyContent = renderToString(bodyContent);
    requestIdVerificationStore.sendRequestIdVerificationEmail(currentBorrower?.emailAddress, subject, emailBodyContent, () => {
      globalStore.notificationStore.showSuccessNotification({
        message: 'Email was sent to borrower!',
      });
    });
  };

  const handleConfirm = () => {
    setConfirmOpen(false);
    handleSendEmail();
  };

  if (!messageConfig?.link) {
    return null;
  }

  const email = currentBorrower?.emailAddress;

  return (
    <>
      <Box className={classes.card}>
        <Box className={classes.linkIcon}>
          <LinkOutlined />
        </Box>
        <Box className={classes.details}>
          <Typography className={classes.title}>New ID verification link</Typography>
          <Typography className={classes.subtitle} title={messageConfig.link}>
            {email && (
              <>
                Sends to <span className={classes.email}>{email}</span>{' · '}
              </>
            )}
            <span className={classes.link}>{messageConfig.link}</span>
          </Typography>
        </Box>
        <Tooltip title="Copy link">
          <IconButton
            className={classes.copyButton}
            onClick={handleCopy}
            data-testid="copyIdVerificationUrlButton"
          >
            <FileCopyOutlined fontSize="small" />
          </IconButton>
        </Tooltip>
        <MuiButton
          variant="contained"
          color="primary"
          className={classes.emailButton}
          startIcon={<MailOutline />}
          onClick={() => setConfirmOpen(true)}
          data-testid="sendIdVerificationButton"
        >
          Email borrower
        </MuiButton>
      </Box>
      <ConfirmDialog
        open={confirmOpen}
        title="Confirmation"
        body={`The link will be emailed to ${email ?? 'the borrower'}. Please confirm.`}
        handleClose={() => setConfirmOpen(false)}
        handleCancel={() => setConfirmOpen(false)}
        handleConfirm={handleConfirm}
      />
    </>
  );
});
